<template>
  <div
    v-if="!hideHeaderMenuForUnavailableDemo"
    class="header__menu"
    :class="{ 'learn-tooltip pointer-none': step === selectedStep }"
  >
    <button
      v-click-outside="hide"
      class="header__icon-link body-text body-text-large-400"
      :class="[{ clicked__button: opened }, { 'is-disabled': !visibleMenuItems.length }]"
      @click="toggle"
    >
      <icon
        class="img-icon-fill"
        data="@icon/menu.svg"
        :fill="true"
        width="var(--icon-24)"
        height="var(--icon-24)"
      />
    </button>

    <div
      v-if="showMenu"
      class="header__menu__box"
    >
      <TooltipLearn
        placement="left"
        placementSub="left"
        :step="step"
        :show="selectedStep === step"
        :autoHide="selectedStep !== step"
        :distance="30"
        :tooltipText="$t(`learnTooltipText.${step}`)"
        class="header__menu__tips"
      >
        <template #tooltip-label>
          <ul class="header__menu__list">
            <router-link
              v-for="(item, index) in visibleMenuItems"
              :key="index"
              class="header__menu__item"
              :to="item.to"
            >
              <icon
                :data="item.icon"
                :fill="item.fill"
                class="header__menu__icon"
                :color="iconColor"
                width="var(--icon-32)"
                height="var(--icon-32)"
              />
              {{ item.label }}
            </router-link>
          </ul>
        </template>
      </TooltipLearn>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import idea from "@icon/idea.svg"
import retrospective from "@icon/retrospective.svg"
import roadmap from "@icon/roadmap.svg"
import storyPoker from "@icon/storyPoker.svg"
import { directive as ClickOutside } from "click-outside-vue3"
import { Getter } from "s-vuex-class"
import { VNode } from "vue"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import TooltipLearn from "@/components/Dropdown/TooltipLearn.vue"

import { Project, Team, TipsStepType, User } from "@/models"
import { Getters } from "@/store"

interface IMenu {
  icon: VNode
  iconColor?: string
  label: string
  to: any
  fill: boolean
  visible: boolean
}
@Options({
  name: "HeaderMenu",
  components: { BaseSubHeading, TooltipLearn },
  directives: {
    ClickOutside
  }
})
export default class HeaderMenu extends Vue {
  @Getter(Getters.GET_CURRENT_PROJECT_ID) projectId: string
  @Getter(Getters.GET_USER_CURRENT_PROFILE) user: User
  @Getter(Getters.GET_SESSION_TEAM) selectedTeam: Team
  @Getter(Getters.GET_SELECTED_TIPS_STEP) readonly selectedStep: TipsStepType
  @Getter(Getters.THEME_IS_DARK) isDark: boolean
  @Getter(Getters.GET_PROJECT) readonly project: Project

  opened = false
  showAdminMenu = true
  step: TipsStepType = "headerMenu"

  get showMenu() {
    return this.opened || this.selectedStep === this.step
  }

  get menuUser() {
    const routeProjectUrlName = this.$route.query.p || this.project?.urlName

    return [
      {
        icon: roadmap,
        label: "Roadmap",
        to: {
          name: "roadmap",
          params: routeProjectUrlName ? { projectId: routeProjectUrlName } : {}
        },
        fill: true,
        visible: this.$route.params.accountId
      },
      {
        icon: idea,
        label: "Ideas",
        to: {
          name: "ideas",
          params: routeProjectUrlName ? { projectId: routeProjectUrlName } : {}
        },
        fill: true,
        visible: this.$route.params.accountId && this.$route.params.projectId
      },
      {
        icon: retrospective,
        label: "Retrospectives",
        to: {
          name: "retros",
          params: {
            projectId: this.project.urlName
          }
        },
        fill: true,
        visible: this.$route.params.accountId && this.$route.params.projectId
      },
      {
        icon: storyPoker,
        label: "Story Poker",
        to: {
          name: "storypoker",
          params: {
            projectId: this.project.urlName
          }
        },
        fill: true,
        visible:
          this.$route.params.accountId && this.$route.params.projectId && this.$route.params.teamId
      }
    ]
  }

  selectedItemTo = ""

  get hideHeaderMenuForUnavailableDemo() {
    return (
      this.$route.name === "projects" &&
      this.user?.settings?.showDemo &&
      (!this.$route.query.p || this.$wait.is("createDemoProject"))
    )
  }

  get visibleMenuItems() {
    return this.menuUser.filter(item => item.visible)
  }

  get currentTeamName() {
    return this.$route.params?.teamId ?? ""
  }

  get iconColor() {
    return this.isDark ? "#AFB4C0" : "#81889B"
  }

  toggle() {
    this.opened = !this.opened
  }

  hide() {
    this.opened = false
  }
}
</script>
